import { Injectable, DestroyRef, effect, inject, Injector, untracked } from '@angular/core';
import { ActivatedRouteSnapshot, Data, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  adminTenants,
  APP_CONFIG,
  AppConfig,
  AppSource,
  resolveBrandTitle,
} from '@application/config';
import { LocalStorageFacade } from '@application/localstorage';
import { PersonNounService } from '@settings/data-access';
import { translatePersonNounLabel } from '@shared/person-noun/pipe';

const DEFAULT_APP_TITLE = 'Actuar';

const APP_SOURCE_TITLES: Partial<Record<AppSource, string>> = {
  [AppSource.None]: DEFAULT_APP_TITLE,
  [AppSource.Actuar]: 'Actuar',
  [AppSource.Treino]: 'Treino',
  [AppSource.Afig]: 'AFIG',
  [AppSource.FacilFit]: 'FácilFit',
  [AppSource.PagueLa]: 'Pague.lá',
  [AppSource.Ediz]: 'Ediz',
};

@Injectable({ providedIn: 'root' })
export class BreadcrumbTitleStrategy extends TitleStrategy {
  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appConfig = inject<AppConfig>(APP_CONFIG, { optional: true });
  private readonly localStorageFacade = inject(LocalStorageFacade);
  private readonly injector = inject(Injector);
  private lastSnapshot: RouterStateSnapshot | null = null;

  constructor() {
    super();

    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.lastSnapshot) {
          this.updateTitle(this.lastSnapshot);
        } else {
          this.titleService.setTitle(this.composeTitle());
        }
      });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastSnapshot = snapshot;

    const breadcrumbLabel = this.extractBreadcrumbLabel(snapshot.root);
    if (breadcrumbLabel) {
      this.ensurePersonNounRefresh();
      const translatedLabel = translatePersonNounLabel(
        this.injector.get(PersonNounService),
        breadcrumbLabel
      );
      this.titleService.setTitle(this.composeTitle(translatedLabel || breadcrumbLabel));
      return;
    }

    const title = this.buildTitle(snapshot);
    if (title) {
      const translatedTitle = this.translateService.instant(title);
      this.titleService.setTitle(this.composeTitle(translatedTitle || title));
      return;
    }

    this.titleService.setTitle(this.composeTitle());
  }

  /**
   * The person-noun title resolves from PersonSettings, which usually arrives
   * after the first NavigationEnd. Re-run updateTitle whenever the setting
   * changes. Created lazily (never in the constructor) so the settings store
   * is not instantiated during Router bootstrap (NG0200).
   */
  private personNounRefreshInitialized = false;

  private ensurePersonNounRefresh(): void {
    if (this.personNounRefreshInitialized) {
      return;
    }
    this.personNounRefreshInitialized = true;

    const personNounService = this.injector.get(PersonNounService);
    effect(
      () => {
        personNounService.key(); // tracks the PersonTreatment setting signal
        untracked(() => {
          if (this.lastSnapshot) {
            this.updateTitle(this.lastSnapshot);
          }
        });
      },
      { injector: this.injector }
    );
  }

  private composeTitle(sectionTitle?: string | null): string {
    const prefix = this.resolveAppTitle();
    if (sectionTitle && sectionTitle.trim().length > 0) {
      return `${prefix} | ${sectionTitle.trim()}`;
    }
    return prefix;
  }

  private resolveAppTitle(): string {
    if (!!this.appConfig && this.appConfig.appSource !== AppSource.Accounts && this.appConfig.appSource !== AppSource.None) {
      const tenantId = JSON.parse(this.localStorageFacade.getItem('authorization'))?.TenantId;
      const admin = adminTenants?.find(tenant => !!tenantId && tenant?.account === tenantId);

      if (admin?.name) return admin.name;
    }

    const configuredTitle = (
      window as { APP_CONFIG?: { title?: string } }
    ).APP_CONFIG?.title?.trim();
    if (configuredTitle) {
      return configuredTitle;
    }

    if (!this.appConfig) {
      return DEFAULT_APP_TITLE;
    }

    if (this.appConfig.appSource === AppSource.None) {
      return DEFAULT_APP_TITLE;
    }

    return resolveBrandTitle(this.appConfig) ?? APP_SOURCE_TITLES[this.appConfig.appSource] ?? DEFAULT_APP_TITLE;
  }

  private extractBreadcrumbLabel(route: ActivatedRouteSnapshot | null): string | null {
    if (!route) {
      return null;
    }

    const isLastChild = route.firstChild === null;
    if (!isLastChild) {
      return this.extractBreadcrumbLabel(route.firstChild);
    }

    const titleData = this.getBreadcrumbData(route.routeConfig?.data ?? route.data);

    if (titleData && typeof titleData === 'string') {
      return titleData;
    }

    if (titleData && typeof titleData === 'object' && 'label' in titleData) {
      return titleData.label as string;
    }

    return null;
  }

  private getBreadcrumbData(data: Data | undefined): unknown {
    if (!data) {
      return null;
    }
    const breadcrumb = data['breadcrumb'];
    const title = data['title'];

    return title ?? breadcrumb;
  }
}
